/* eslint-disable max-lines */
import Button from '@components/atoms/ui-kit/button.tsx';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StatusBadge } from '@components/atoms/ui-kit/badge';
import { useThemeContext } from '@utils/hooks/use-theme-context';
import toast from 'react-hot-toast';
import { useSearchParams } from 'react-router-dom';
import { AlertTriangle, Plus, Receipt } from 'lucide-react';
import ConfirmationModal from '@components/atoms/modals/confirmation-modal.tsx';
import AddOneOffModal from '@components/back-office/usage-revenue/add-one-off-modal';
import PageHeader from '@components/layout/page-header';
import { formatCurrency } from '@utils/helpers/currency';
import { SupportedCurrency } from '@utils/types/organization/organization';
import DataGridV2, {
  type DataGridColumn
} from '@components/atoms/datatable/data-grid-v2';

import {
  Billable,
  Billables,
  useGetBillablesBo
} from '@utils/api/back-office/billables/use-get-billables-bo';
import { useInvoiceBillables } from '@utils/api/back-office/billables/use-invoice-billables.tsx';
import DateInput from '@components/atoms/ui-kit/date-input';
import { Axios } from '@utils/api/axios-instance';
import { GET_URL_NEW } from '@utils/api/endpoints';
import { useNewAppSearchParams } from '@utils/hooks/use-new-app-search-params';

export default function BillablesBo() {
  const { getColour } = useThemeContext();
  const { t } = useTranslation();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isOneOffModalOpen, setIsOneOffModalOpen] = useState(false);
  const [isFutureDate, setIsFutureDate] = useState(false);
  const [periodEnd, setPeriodEnd] = useState<string | undefined>(undefined);
  const [isInvoicing, setIsInvoicing] = useState(false);

  const { mutate: invoiceBillables } = useInvoiceBillables(() =>
    setIsModalOpen(false)
  );

  const { data: billables, isLoading } = useGetBillablesBo(periodEnd);

  // Shared with the main list query so "Invoice all" uses the same
  // filters/sort the user is currently viewing.
  const { searchParams: appSearchParams } = useNewAppSearchParams();
  const [sp] = useSearchParams();
  const sortField =
    (appSearchParams.sort as string | undefined) ?? 'billable_cost';
  const sortDirection =
    (appSearchParams.direction as 'asc' | 'desc' | undefined) ?? 'desc';
  const allQ = sp.getAll('q').filter(Boolean);

  // Fetch every billable the user's current filters cover, not just the
  // current page. Previously the POST sent `billables.result.map(id)`,
  // which was only page 1 (default limit=25) — anything on page 2+ was
  // silently skipped.
  //
  // The backend caps `limit` at 200 (422 otherwise), so we loop pages
  // of 200 until we've covered `total_results`. 465 open billables in
  // production → three round trips, acceptable before the POST.
  // Returns `null` on any page failure; the caller shows the toast.
  const fetchAllInvoiceableIds = async (): Promise<number[] | null> => {
    const PAGE_SIZE = 200;
    const ids: number[] = [];
    let offset = 0;
    try {
      for (;;) {
        const { billablesWith } = GET_URL_NEW({
          offset,
          limit: PAGE_SIZE,
          q: allQ.length > 0 ? allQ : undefined,
          sort: sortField,
          direction: sortDirection
        });
        const url = billablesWith(periodEnd ? { period_end: periodEnd } : {});
        const { data } = await Axios.get<Billables>(url);
        for (const b of data.result) ids.push(b.id);
        const nextOffset = offset + data.result.length;
        // Stop when the server returned fewer rows than we asked for,
        // or when we've reached the server's advertised total.
        if (
          data.result.length < PAGE_SIZE ||
          nextOffset >= data.total_results
        ) {
          return ids;
        }
        offset = nextOffset;
      }
    } catch {
      return null;
    }
  };

  const handleInvoice = async () => {
    // Guard against an empty POST — if the current filter yields no
    // billables, don't ask the backend to invoice nothing.
    if (!billables?.result || billables.result.length === 0) {
      toast.error(t('no-billables-to-invoice'));
      return;
    }

    setIsInvoicing(true);

    const ids = await fetchAllInvoiceableIds();
    if (ids === null) {
      // Pre-fetch failed. The global mutation handler won't fire for
      // this (it's a plain GET), so toast locally with a hint that the
      // user should refresh.
      toast.error(t('failed-to-create-invoice'));
      setIsInvoicing(false);
      return;
    }
    if (ids.length === 0) {
      toast.error(t('no-billables-to-invoice'));
      setIsInvoicing(false);
      return;
    }

    invoiceBillables(
      { ids },
      {
        onSuccess: data => {
          const { invoices, message } = data.result;
          if (invoices.length > 0) {
            toast.success(t('invoice-successfully-created'));
          }
          if (message) {
            // Purchasers that were not invoiced stay on the billables list;
            // say which, instead of a plain success.
            toast.error(
              invoices.length > 0
                ? `${t('some-billables-not-invoiced')}\n${message}`
                : message,
              { duration: 30000 }
            );
          }
          setIsModalOpen(false);
          setIsInvoicing(false);
        },
        onError: () => {
          // No toast here — the global `handleMutationError` already
          // extracts and shows the message via `getErrorMessage`. A
          // second toast duplicates (seen when the request times out:
          // user got both "Failed to create invoice" AND "An error
          // occurred while invoicing billables"). Only clean up local
          // state here.
          setIsInvoicing(false);
        }
      }
    );
  };

  // still keep the external date filtering (avoids clashing with DataGridV2's q)
  const filteredBillables = useMemo(() => {
    if (!billables?.result) return [];
    return billables.result.filter(b =>
      periodEnd ? b.period_end <= periodEnd : true
    );
  }, [billables, periodEnd]);

  // Earliest period_start → latest period_end across the current page.
  // Shown in the confirmation modal so the admin can see which billing
  // window they're about to invoice. Uses the paginated page because
  // that's what drives the summary number; the actual POST will cover
  // every page via `fetchAllInvoiceableIds` and the backend applies the
  // same filters, so this range is a true preview.
  const invoiceRange = useMemo(() => {
    if (filteredBillables.length === 0) return null;
    let from = filteredBillables[0].period_start;
    let to = filteredBillables[0].period_end;
    for (const b of filteredBillables) {
      if (b.period_start < from) from = b.period_start;
      if (b.period_end > to) to = b.period_end;
    }
    return { from, to };
  }, [filteredBillables]);

  const sumByCurrency = useMemo(() => {
    const totals = new Map<SupportedCurrency, number>();
    for (const b of filteredBillables) {
      const currency = b.currency ?? 'EUR';
      totals.set(
        currency,
        (totals.get(currency) ?? 0) + Number(b.billable_cost ?? 0)
      );
    }
    return [...totals.entries()];
  }, [filteredBillables]);

  type Row = {
    id: number;
    purchaser: string;
    organization: string;
    currency: SupportedCurrency;
    billable_cost: number;
    period_start: string;
    period_end: string;
    subscription_id: number;
    usage_stt: number;
    usage_alignment: number;
    usage_translation: number;
    current_cost: number;
    custom_models_trained: number;
    custom_models_hosted: number;
    stt_templates_exported: number;
    billing_cycle_status: string;
    billing_cycle_locked: string;
    billing_cycle_id: number | null;
  };

  const rows = useMemo<Row[]>(
    () =>
      filteredBillables.map((b: Billable) => ({
        id: b.id,
        purchaser: b.purchaser,
        organization: b.organization,
        currency: b.currency ?? 'EUR',
        billable_cost: b.billable_cost
          ? Math.ceil(Number(b.billable_cost) * 100) / 100
          : 0,
        period_start: b.period_start,
        period_end: b.period_end,
        subscription_id: b.subscription_id,
        usage_stt: b.usage_stt,
        usage_alignment: b.usage_alignment,
        usage_translation: b.usage_translation,
        current_cost: b.current_cost
          ? Math.ceil(Number(b.current_cost) * 100) / 100
          : 0,
        custom_models_trained: b.custom_models_trained,
        custom_models_hosted: b.custom_models_hosted,
        stt_templates_exported: b.stt_templates_exported,
        billing_cycle_status: b.billing_cycle?.status ?? '',
        billing_cycle_locked: b.billing_cycle?.is_locked ? t('yes') : t('no'),
        billing_cycle_id: b.billing_cycle?.id ?? null
      })),
    [filteredBillables, t]
  );

  const columns = useMemo<DataGridColumn<Row>[]>(
    () => [
      {
        id: 'purchaser',
        header: t('purchaser'),
        field: 'purchaser',
        accessorKey: 'purchaser',
        minSize: 140,
        enableFiltering: true,
        enableSorting: true,
        cell: ({ getValue }) => String(getValue() ?? '')
      },
      {
        id: 'organization',
        header: t('organization'),
        field: 'organization',
        accessorKey: 'organization',
        minSize: 140,
        enableFiltering: true,
        enableSorting: true,
        cell: ({ getValue }) => String(getValue() ?? '')
      },
      {
        id: 'billable_cost',
        header: t('billable-cost'),
        field: 'billable_cost',
        accessorKey: 'billable_cost',
        maxWidth: 110,
        enableFiltering: false,
        filterVariant: 'number',
        enableSorting: true,
        cell: ({ getValue, row }) =>
          formatCurrency(Number(getValue() ?? 0), row.original.currency)
      },
      {
        id: 'period_start',
        header: t('period-start'),
        field: 'period_start',
        accessorKey: 'period_start',
        maxWidth: 110,
        enableFiltering: false,
        enableSorting: false,
        cell: ({ getValue }) => String(getValue() ?? '')
      },
      {
        id: 'period_end',
        header: t('period-end'),
        field: 'period_end',
        accessorKey: 'period_end',
        maxWidth: 110,
        enableFiltering: false,
        enableSorting: false,
        cell: ({ getValue }) => String(getValue() ?? '')
      },
      {
        id: 'usage_stt',
        header: t('stt-usage'),
        field: 'usage_stt',
        accessorKey: 'usage_stt',
        maxWidth: 90,
        enableFiltering: false,
        filterVariant: 'number',
        enableSorting: false,
        cell: ({ getValue }) => String(getValue() ?? '')
      },
      {
        id: 'usage_alignment',
        header: t('alignment'),
        field: 'usage_alignment',
        accessorKey: 'usage_alignment',
        maxWidth: 100,
        enableFiltering: false,
        filterVariant: 'number',
        enableSorting: false,
        cell: ({ getValue }) => String(getValue() ?? '')
      },
      {
        id: 'usage_translation',
        header: t('translation'),
        field: 'usage_translation',
        accessorKey: 'usage_translation',
        maxWidth: 100,
        enableFiltering: false,
        filterVariant: 'number',
        enableSorting: false,
        cell: ({ getValue }) => String(getValue() ?? '')
      },
      {
        id: 'current_cost',
        header: t('current-cost'),
        field: 'current_cost',
        accessorKey: 'current_cost',
        maxWidth: 110,
        enableFiltering: false,
        filterVariant: 'number',
        enableSorting: false,
        cell: ({ getValue, row }) =>
          formatCurrency(Number(getValue() ?? 0), row.original.currency)
      },
      {
        id: 'custom_models_trained',
        header: t('models-trained'),
        field: 'custom_models_trained',
        accessorKey: 'custom_models_trained',
        maxWidth: 120,
        enableFiltering: false,
        filterVariant: 'number',
        enableSorting: false,
        cell: ({ getValue }) => String(getValue() ?? '')
      },
      {
        id: 'custom_models_hosted',
        header: t('models-hosted'),
        field: 'custom_models_hosted',
        accessorKey: 'custom_models_hosted',
        maxWidth: 120,
        enableFiltering: false,
        filterVariant: 'number',
        enableSorting: false,
        cell: ({ getValue }) => String(getValue() ?? '')
      },
      {
        id: 'stt_templates_exported',
        header: t('templates-exported'),
        field: 'stt_templates_exported',
        accessorKey: 'stt_templates_exported',
        maxWidth: 140,
        enableFiltering: false,
        filterVariant: 'number',
        enableSorting: false,
        cell: ({ getValue }) => String(getValue() ?? '')
      },
      {
        id: 'billing_cycle_status',
        header: t('cycle-status'),
        field: 'billing_cycle_status',
        accessorKey: 'billing_cycle_status',
        maxWidth: 110,
        enableFiltering: false,
        enableSorting: false,
        cell: ({ getValue }) => {
          const status = String(getValue() ?? '');
          if (!status) return null;
          return <StatusBadge status={status} />;
        }
      },
      {
        id: 'billing_cycle_locked',
        header: t('cycle-locked'),
        field: 'billing_cycle_locked',
        accessorKey: 'billing_cycle_locked',
        maxWidth: 100,
        enableFiltering: false,
        filterVariant: 'select',
        filterOptions: [
          { label: t('yes'), value: t('yes') },
          { label: t('no'), value: t('no') }
        ],
        enableSorting: false,
        cell: ({ getValue }) => {
          const label = String(getValue() ?? '');
          return (
            <span
              className="inline-flex items-center rounded-full border border-hairline bg-app-soft px-2.5 py-0.5 text-xs font-medium"
              style={{ color: getColour('normalText') }}
            >
              {label}
            </span>
          );
        }
      }
    ],
    [t, getColour]
  );

  const getMaxSelectableDate = () => {
    const future = new Date();
    future.setDate(future.getDate() + 30);
    return future.toISOString().split('T')[0];
  };

  // Filters row for PageHeader
  const filtersRow = (
    <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
      <div className="sm:max-w-xs">
        <label
          className="block text-xs font-medium mb-1.5"
          style={{ color: getColour('inactiveText') }}
        >
          {t('apply-billing-date')}
        </label>
        <DateInput
          id="billingPeriodEnd"
          value={periodEnd ?? ''}
          max={getMaxSelectableDate()}
          withShortcuts
          size="small"
          onChange={v => {
            const value = v || undefined;
            setPeriodEnd(value);

            if (value) {
              const selectedDate = new Date(value);
              const today = new Date();
              selectedDate.setHours(0, 0, 0, 0);
              today.setHours(0, 0, 0, 0);
              setIsFutureDate(selectedDate > today);
            } else {
              setIsFutureDate(false);
            }
          }}
          wrapperClass="w-full"
        />
      </div>

      {/* Summary - structured like the left side with label + content */}
      <div className="flex items-end gap-3">
        <div>
          <label
            className="block text-xs font-medium mb-1.5"
            style={{ color: getColour('inactiveText') }}
          >
            {t('total-to-invoice')}
          </label>
          <div className="flex items-center gap-2 px-3 py-2 rounded-full border border-hairline bg-app-soft h-[38px]">
            <Receipt
              className="h-4 w-4"
              style={{ color: getColour('inactiveText') }}
            />
            <span
              className="text-sm font-semibold"
              style={{ color: getColour('normalText') }}
            >
              {sumByCurrency.length === 0
                ? formatCurrency(0)
                : sumByCurrency
                    .map(([currency, total]) => formatCurrency(total, currency))
                    .join(' + ')}
            </span>
          </div>
        </div>

        {isFutureDate && (
          <div className="flex items-center gap-1.5 text-xs text-yellow-500 pb-2">
            <AlertTriangle className="h-3.5 w-3.5" />
            <span>{t('billings-may-not-be-definitive-for-future-dates')}</span>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="bg-app flex-1">
      <ConfirmationModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onConfirm={handleInvoice}
        size="small"
        title={t('billing-confirmation')}
        caption={t('are-you-sure-you-want-to-invoice-all-customers')}
        confirmLabel={isInvoicing ? t('Invoicing...') : t('Confirm')}
        cancelLabel={t('cancel')}
        confirmDisabled={isInvoicing}
        destructive={false}
        autoFocusConfirm
      >
        {invoiceRange && (
          <div
            className="rounded-xl border px-3 py-2 text-sm mt-1 flex items-center justify-between gap-3"
            style={{
              borderColor: getColour('border'),
              backgroundColor: getColour('secondLayer'),
              color: getColour('normalText')
            }}
          >
            <span
              className="text-xs uppercase tracking-wider font-semibold"
              style={{ color: getColour('inactiveText') }}
            >
              {t('billing-period')}
            </span>
            <span className="tabular-nums">
              {invoiceRange.from} → {invoiceRange.to}
            </span>
          </div>
        )}
      </ConfirmationModal>

      <AddOneOffModal
        isOpen={isOneOffModalOpen}
        onClose={() => setIsOneOffModalOpen(false)}
        onSuccess={() => {
          // Refetch billables when a one-off is added
        }}
      />
      <div className="mt-2">
        <PageHeader
          title={t('billables')}
          caption={t('manage-billing-periods-and-invoice-customers')}
          filters={filtersRow}
        >
          <Button
            onClick={() => setIsOneOffModalOpen(true)}
            label={t('add-one-off')}
            variant="secondary"
            size="small"
            icon={<Plus className="h-4 w-4" />}
          />
          <Button
            onClick={() => setIsModalOpen(true)}
            label={t('invoice-all')}
            disabled={isLoading || billables?.result?.length === 0}
            size="small"
          />
        </PageHeader>

        <div className="w-full mt-4">
          <DataGridV2<Row>
            data={rows}
            columns={columns}
            enableRowSelection={false}
            enablePagination
            manualPagination
            enableSorting
            manualSorting
            enableFiltering
            manualFiltering
            totalItems={billables?.total_results ?? rows.length}
            pageSize={25}
            pageSizeOptions={[25, 50, 75, 100]}
            showColumnFilters={false}
            filterMode="popover"
            height="auto"
            dense
            grid={false}
            striped
            loading={isLoading || !billables}
            defaultSort={[{ id: 'billable_cost', desc: true }]}
            getRowId={row => String(row.id)}
          />
        </div>
      </div>
    </div>
  );
}
